import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_USERS, FETCH_TEAMS } from '../../../../gql/queries/users';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { GET_CONCESSION_BY_ID } from '../../../../gql/queries/geoPositions/roads';

// Utils
import { AuthContextType } from '../../../../hooks/useAuth';

// Constants
import { formUserManagementNaming } from '../../../../components/forms/AddUserForm/constants';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';

const loadTableData = async (
  userData: AuthContextType | null,
  concessions: { id: string; name: string }[],
  teams: { id: string; name: string; userIDs: string[] }[]
) => {
  try {
    const usersToReturnWithConcession = [];
    if (userData?.user) {
      let usersToReturn;
      const {
        data: { users },
      } = await client.query({
        query: FETCH_USERS,
      });

      if (!users || users.length === 0) {
        const {
          data: { users },
        } = await client.query({
          query: FETCH_USERS,
        });

        usersToReturn = users;
      } else {
        usersToReturn = users;
      }

      for (const user of usersToReturn) {
        let concession;
        if (user.concessionIDs && user.concessionIDs.length === 1) {
          concession = await client.query({
            query: GET_CONCESSION_BY_ID,
            variables: {
              id: user.concessionIDs[0],
            },
          });
        }

        // Find teams by ID from user.teamIDs
        const userTeams = teams
          .filter((team) => user.teamIDs?.includes(team.id))
          .map((team) => ({ id: team.id, name: team.name }));

        usersToReturnWithConcession.push({
          id: user.id,
          name: user.name,
          surName: user.surName,
          email: user.email,
          rolActive: user.rolActive,
          concession:
            user.concessionIDs && user.concessionIDs.length > 0
              ? user.concessionIDs.length > 1
                ? concessions.map((concessionInner) => concessionInner.name)
                : [concession?.data.concession.name]
              : [],
          state: user.isActive ? 'active' : 'inactive',
          isActive: user.isActive,
          teams: userTeams,
        });
      }
    }
    return usersToReturnWithConcession;
  } catch (err) {
    Sentry.captureException(err);
    console.log('> User management loading error:', err);
    return [];
  }
};

export const UsersManagementLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const {
    data: { concessions },
  } = await client.query({
    query: GET_CONCESSIONS,
  });

  // ✅ M2M: Fetch teams for user assignment
  const {
    data: { teams },
  } = await client.query({
    query: FETCH_TEAMS,
    fetchPolicy: 'network-only',
  });

  const data = await loadTableData(userData, concessions, teams || []);

  const activeUsers = data.filter(
    (user: { isActive: boolean }) => user.isActive
  );
  const inactiveUsers = data.filter(
    (user: { isActive: boolean }) => !user.isActive
  );

  let cacheData =
    sessionStorage.getItem(formUserManagementNaming.NEW_USER_MANAGEMENT_FORM) ||
    {};

  if (Object.keys(cacheData).length > 0) {
    cacheData = JSON.parse(decompressSessionStorage(cacheData as string));
  }

  const concessionsWithAll = [
    { id: 'all', name: 'Totes les concessions' },
    ...concessions,
  ];

  return {
      activeUsers,
      inactiveUsers,
      cacheData: {
        ...cacheData,
        concessions: concessionsWithAll,
        teams: teams || [], // ✅ M2M: Provide teams to form
      },
    };
};

  return { data: load() };
};
